import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROW_H = 44;

const toMin = (s) => {
  const [h, m] = s.split(":").map(Number);
  return h * 60 + m;
};
const toStr = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

const shiftDate = (iso, days) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtLong = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

export default function AgendaGrid({ date, appts, onPickDate, onSlotClick, onBlockClick }) {
  const [barbers, setBarbers] = useState([]);
  const [hours, setHours] = useState(null);

  useEffect(() => {
    axios.get(`${API}/barbers`).then((r) => setBarbers(r.data)).catch(() => {});
    axios.get(`${API}/hours`).then((r) => setHours(r.data)).catch(() => {});
  }, []);

  const weekday = useMemo(() => {
    const [y, m, d] = date.split("-").map(Number);
    return (new Date(y, m - 1, d).getDay() + 6) % 7;
  }, [date]);

  const windows = useMemo(() => {
    if (!hours) return [];
    const day = hours[String(weekday)];
    return day && !day.closed ? [...day.windows].sort((a, b) => toMin(a[0]) - toMin(b[0])) : [];
  }, [hours, weekday]);

  const navBtn = "focus-gold p-2 border border-white/15 text-smoke hover:border-gold hover:text-gold transition-colors";

  const header = (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10">
      <div className="flex items-center gap-2">
        <button onClick={() => onPickDate(shiftDate(date, -1))} aria-label="Dia anterior" data-testid="agenda-prev-day" className={navBtn}>
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button onClick={() => onPickDate(todayISO())} data-testid="agenda-today" className="focus-gold font-bebas tracking-widest text-sm px-4 py-2 border border-white/15 text-smoke hover:border-gold hover:text-gold transition-colors">
          Hoje
        </button>
        <button onClick={() => onPickDate(shiftDate(date, 1))} aria-label="Próximo dia" data-testid="agenda-next-day" className={navBtn}>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <p className="font-bebas text-lg md:text-xl tracking-wide text-cream capitalize truncate" data-testid="agenda-grid-date">{fmtLong(date)}</p>
    </div>
  );

  if (!hours || !barbers.length) {
    return <div className="mt-8 border border-white/10 bg-surface" data-testid="agenda-grid">{header}<p className="py-10 text-center text-sm text-smoke">Carregando grade...</p></div>;
  }

  if (!windows.length) {
    return (
      <div className="mt-8 border border-white/10 bg-surface" data-testid="agenda-grid">
        {header}
        <p className="py-10 text-center text-sm text-eraorange" data-testid="agenda-grid-closed">Fechado neste dia.</p>
      </div>
    );
  }

  const start = Math.min(...windows.map((w) => toMin(w[0])));
  const end = Math.max(...windows.map((w) => toMin(w[1])));
  const totalH = ((end - start) / 30) * ROW_H;

  const gaps = [];
  for (let i = 1; i < windows.length; i++) {
    const g0 = toMin(windows[i - 1][1]);
    const g1 = toMin(windows[i][0]);
    if (g1 > g0) gaps.push([g0, g1]);
  }

  const hourMarks = [];
  for (let t = Math.ceil(start / 60) * 60; t < end; t += 60) hourMarks.push(t);

  return (
    <div className="mt-8 border border-white/10 bg-surface" data-testid="agenda-grid">
      {header}
      <div className="overflow-x-auto">
        <div className="flex">
          <div className="w-10 md:w-14 shrink-0 border-r border-white/10">
            <p className="py-2 border-b border-white/10 bg-ink/50 text-center font-bebas text-base md:text-lg select-none" aria-hidden="true">&nbsp;</p>
            <div className="relative" style={{ height: totalH }}>
              {hourMarks.map((t) => (
                <span key={t} className="absolute right-1 md:right-2 text-[10px] md:text-[11px] font-semibold text-smoke -translate-y-1/2" style={{ top: ((t - start) / 30) * ROW_H }}>
                  {toStr(t)}
                </span>
              ))}
            </div>
          </div>
          {barbers.map((b) => {
            const mine = appts.filter((a) => a.barber_id === b.id && a.status !== "cancelled");
            return (
              <div key={b.id} className="flex-1 min-w-0 border-r border-white/10 last:border-r-0" data-testid={`agenda-col-${b.id}`}>
                <p className="text-center font-bebas text-base md:text-lg tracking-widest text-cream py-2 border-b border-white/10 bg-ink/50 truncate px-1">{b.name}</p>
                <div
                  className="relative cursor-pointer"
                  style={{ height: totalH }}
                  title="Clique num horário livre pra agendar"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const mins = start + Math.floor((e.clientY - rect.top) / ROW_H) * 30;
                    const inWindow = windows.some((w) => toMin(w[0]) <= mins && mins + 30 <= toMin(w[1]));
                    if (!inWindow) return;
                    const taken = appts.some((a) => a.barber_id === b.id && a.status !== "cancelled" && toMin(a.time) < mins + 30 && toMin(a.time) + a.duration > mins);
                    if (taken) return;
                    onSlotClick && onSlotClick(b, toStr(mins));
                  }}
                >
                  {Array.from({ length: (end - start) / 30 }, (_, i) => (
                    <div key={i} className={`absolute inset-x-0 ${i % 2 === 0 ? "border-t border-white/10" : "border-t border-dotted border-white/5"}`} style={{ top: i * ROW_H }} />
                  ))}
                  {gaps.map(([g0, g1]) => (
                    <div
                      key={g0}
                      className="absolute inset-x-0 bg-white/[0.04] flex items-center justify-center"
                      style={{ top: ((g0 - start) / 30) * ROW_H, height: ((g1 - g0) / 30) * ROW_H }}
                    >
                      <span className="text-[10px] uppercase tracking-[0.25em] text-smoke/60">intervalo</span>
                    </div>
                  ))}
                  {mine.map((a) => {
                    const top = ((toMin(a.time) - start) / 30) * ROW_H;
                    const h = Math.max((a.duration / 30) * ROW_H - 4, 24);
                    const confirmed = a.status === "confirmed";
                    return (
                      <div
                        key={a.id}
                        data-testid={`agenda-block-${a.id}`}
                        className={`absolute left-0.5 right-0.5 md:left-1 md:right-1 border-l-2 px-1 md:px-2 py-1 overflow-hidden ${a.blocked ? "border-red-500 bg-red-500/10 cursor-pointer hover:bg-red-500/20" : confirmed ? "border-gold bg-gold/15 cursor-default" : "border-eraorange bg-eraorange/10 cursor-default"}`}
                        style={{ top: top + 2, height: h }}
                        onClick={(e) => { e.stopPropagation(); if (a.blocked && onBlockClick) onBlockClick(a); }}
                        title={a.blocked ? "Horário bloqueado — clique pra desbloquear" : `${a.time} · ${a.name} · ${a.service_name}`}
                      >
                        <p className={`text-[11px] font-bold leading-tight ${a.blocked ? "text-red-400" : "text-gold"}`}>{a.time} — {toStr(toMin(a.time) + a.duration)}</p>
                        <p className="text-xs font-semibold text-cream truncate">{a.blocked ? "Bloqueado" : a.name}</p>
                        {h > 60 && <p className="text-[11px] text-smoke truncate">{a.blocked ? (a.service_name === "Bloqueio" ? "clique pra desbloquear" : a.service_name) : `${a.service_name} · R$ ${a.price}`}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex items-center gap-5 px-4 py-3 border-t border-white/10">
        <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-smoke">
          <span className="w-3 h-3 bg-gold/40 border border-gold inline-block" /> Agendado
        </span>
        <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-smoke">
          <span className="w-3 h-3 border border-white/20 inline-block" /> Livre
        </span>
        <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-smoke">
          <span className="w-3 h-3 bg-red-500/30 border border-red-500 inline-block" /> Bloqueado
        </span>
        <span className="text-[11px] uppercase tracking-[0.2em] text-gold ml-auto">Clique num horário livre pra agendar</span>
      </div>
    </div>
  );
}
